'use client'

// Next.js
import Link from 'next/link'
import { usePathname } from 'next/navigation'

// Headless UI
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from '@headlessui/react'

// Icons
import { ChevronRightIcon } from '@heroicons/react/24/outline'

// Docs components
import { DocSearch } from './doc-search'

// Lib
import { DocNavItem, SearchIndexItem } from '../../lib/docs-nav'

interface DocSidebarProps {
  navigation: DocNavItem[]
  searchIndex: SearchIndexItem[]
  onLinkClick?: () => void
}

/** Inset focus — outward rings are clipped by the sidebar `overflow-y-auto`. */
const navFocus =
  'focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500 focus-visible:outline-none dark:focus-visible:ring-blue-400'

function navLinkClass(isActive: boolean, extra = '') {
  return [
    'block rounded-md px-3 py-2 text-sm transition-colors',
    navFocus,
    isActive
      ? 'bg-primary-100 text-primary-900 dark:bg-primary-900 dark:text-primary-100 font-semibold'
      : 'text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800',
    extra,
  ]
    .filter(Boolean)
    .join(' ')
}

/** Shared label style for links and wrapper expand buttons. */
function navLabelClass(isActive: boolean, extra = '') {
  return navLinkClass(isActive, extra)
}

/** `docs-nav` emits trailing `/`; `usePathname()` only includes it when the app sets `trailingSlash: true`. */
function pathsEqual(
  a: string | null | undefined,
  b: string | null | undefined,
) {
  if (!a || !b) return false
  const norm = (p: string) => (p === '/' ? p : p.replace(/\/+$/, ''))
  return norm(a) === norm(b)
}

function NavItem({
  item,
  depth = 0,
  onLinkClick,
  isInActivePath,
}: {
  item: DocNavItem
  depth?: number
  onLinkClick?: () => void
  isInActivePath: boolean
}) {
  const pathname = usePathname()
  const isActive = item.isPage && pathsEqual(pathname, item.href)
  const hasChildren = item.children && item.children.length > 0

  // Check if any child is in the active path
  const hasActiveChild = (items?: DocNavItem[]): boolean => {
    if (!items) return false
    return items.some(
      (child) =>
        (child.isPage && pathsEqual(pathname, child.href)) ||
        (child.children && hasActiveChild(child.children)),
    )
  }

  const shouldBeOpen =
    hasChildren && (isInActivePath || hasActiveChild(item.children))

  if (!hasChildren) {
    // Leaf node - just render a link (only pages have no children + isPage)
    return (
      <li>
        {item.isPage ? (
          <Link
            href={item.href}
            onClick={onLinkClick}
            className={navLabelClass(isActive)}
            style={{ marginLeft: depth * 12 }}
          >
            {item.title}
          </Link>
        ) : (
          <div
            className={navLabelClass(false)}
            style={{ marginLeft: depth * 12 }}
          >
            {item.title}
          </div>
        )}
      </li>
    )
  }

  // Parent node - render with Disclosure
  return (
    <li>
      <Disclosure key={pathname} defaultOpen={shouldBeOpen}>
        {({ open }) => (
          <>
            {item.isPage ? (
              // Real page with children: chevron expands, title navigates
              <div className="flex items-center">
                <DisclosureButton
                  className={`mr-1 cursor-pointer rounded p-1 hover:bg-slate-200 dark:hover:bg-slate-800 ${navFocus}`}
                  aria-label={`${open ? 'Collapse' : 'Expand'} ${item.title}`}
                >
                  <ChevronRightIcon
                    className={`h-4 w-4 transition-transform ${open ? 'rotate-90' : ''}`}
                  />
                </DisclosureButton>
                <Link
                  href={item.href}
                  onClick={onLinkClick}
                  className={navLabelClass(isActive, 'flex-1')}
                >
                  {item.title}
                </Link>
              </div>
            ) : (
              // Wrapper: whole row is one expand button; chevron column matches page parents
              <DisclosureButton
                className={`flex w-full cursor-pointer items-center rounded-md text-left text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800 ${navFocus}`}
              >
                <span className="mr-1 shrink-0 p-1" aria-hidden>
                  <ChevronRightIcon
                    className={`h-4 w-4 transition-transform ${open ? 'rotate-90' : ''}`}
                  />
                </span>
                <span className="flex-1 py-2 pr-3 text-sm">{item.title}</span>
              </DisclosureButton>
            )}
            <DisclosurePanel>
              <ul className="mt-1 ml-2 space-y-1 border-l-2 border-slate-200 pl-2 dark:border-slate-700">
                {item.children?.map((child) => {
                  const childIsInActivePath =
                    (child.isPage && pathsEqual(pathname, child.href)) ||
                    hasActiveChild(child.children)
                  return (
                    <NavItem
                      key={child.href || child.title}
                      item={child}
                      depth={depth + 1}
                      onLinkClick={onLinkClick}
                      isInActivePath={childIsInActivePath}
                    />
                  )
                })}
              </ul>
            </DisclosurePanel>
          </>
        )}
      </Disclosure>
    </li>
  )
}

export function DocSidebar({
  navigation,
  searchIndex,
  onLinkClick,
}: DocSidebarProps) {
  const pathname = usePathname()

  // Find which items are in the active path
  const isItemInActivePath = (item: DocNavItem): boolean => {
    if (item.isPage && pathsEqual(item.href, pathname)) return true
    if (item.children) {
      return item.children.some(isItemInActivePath)
    }
    return false
  }

  return (
    // Search sits outside the scrollport so its focus ring is never clipped by
    // `overflow-y-auto` (which clips both axes). Only the nav list scrolls.
    <nav className="sticky top-20 flex max-h-[calc(100vh-1rem)] flex-col lg:top-22">
      <div className="shrink-0 p-0.5">
        <DocSearch navigation={navigation} searchIndex={searchIndex} />
      </div>
      <ul className="min-h-0 flex-1 scrollbar-thin space-y-1 overflow-y-auto overscroll-contain pr-2 pb-24 text-sm lg:pb-0">
        {navigation.map((item) => (
          <NavItem
            key={item.href || item.title}
            item={item}
            onLinkClick={onLinkClick}
            isInActivePath={isItemInActivePath(item)}
          />
        ))}
      </ul>
    </nav>
  )
}
